import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { FaSearch } from 'react-icons/fa';
import TsdiClient from 'src/models/AP/Client';
import ICostBreakdownProspectCandidate from '../models/costBreakdownProspectCandidate';
import ICostBreakdownJob from '../models/costBreakdownJob';

export interface ISaveCostBreakdownJob {
    clNo: number;
    email: string;
    cc: string;
    frequency: string;
    dayNo?: number;
    active: boolean;
    includeAdmin: boolean;
}

interface ICostBreakdownJobDialogProps {
    dialogName: string;
    editingJob?: ICostBreakdownJob;
    client?: TsdiClient;
    initialClientNumber?: number;
    onClientNumberBlur: (clientNumber: string) => void;
    onFindClient: () => void;
    prospects: Array<ICostBreakdownProspectCandidate>;
    selected: Record<number, boolean>;
    onToggleProspect: (prId: number) => void;
    onSelectAll: () => void;
    onDeselectAll: () => void;
    onCancel: () => void;
    onSave: (data: ISaveCostBreakdownJob) => void;
}

const FREQUENCY_OPTIONS = [
    { value: 'D', label: 'Daily' },
    { value: 'W', label: 'Weekly' },
    { value: 'M', label: 'Monthly' },
];

const YES_NO_OPTIONS = [
    { value: 'Y', label: 'Yes' },
    { value: 'N', label: 'No' },
];

// Displayed Sunday-first (S/M/T/W/R/F/S) - matches the Sunday=1...Saturday=7 convention
// ScheduleDescription()/the DB already use, and keeps the two 'S' boxes (Sun/Sat) at opposite ends
// of the row instead of adjacent, which was ambiguous to read/click.
const WEEKDAY_BOXES = [
    { day: 1, label: 'S' },
    { day: 2, label: 'M' },
    { day: 3, label: 'T' },
    { day: 4, label: 'W' },
    { day: 5, label: 'R' },
    { day: 6, label: 'F' },
    { day: 7, label: 'S' },
];

// A 6x5 grid (30 boxes) for "Custom" day-of-month - Last/First (see MONTH_DAY_TYPE_OPTIONS) cover
// the unambiguous edge cases, so Custom is free to allow days that don't exist in every month
// (same as Oracle's own 1-31 range) without reintroducing that ambiguity for the common cases.
const MONTH_DAY_BOXES = Array.from({ length: 30 }, (_, i) => i + 1);

const MONTH_DAY_TYPE_OPTIONS: Array<{ value: 'custom' | 'last' | 'first'; label: string }> = [
    { value: 'custom', label: 'Custom' },
    { value: 'last', label: 'Last' },
    { value: 'first', label: 'First' },
];

// Reserves the tallest possible height any Frequency selection can produce - with the Day fieldset
// sitting beside (not above) the 30-box grid, the grid itself (5 rows) is the tall side - so
// switching Daily/Weekly/Monthly never resizes the dialog; shorter states just leave blank space.
const FREQUENCY_DETAIL_MIN_HEIGHT = '14rem';

const DAY_BOX_BASE: React.CSSProperties = {
    width: '2.25rem', height: '2.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid #000', background: '#fff', color: '#000', cursor: 'pointer', userSelect: 'none', fontWeight: 500,
};
const DAY_BOX_SELECTED: React.CSSProperties = { background: '#6393C1', color: '#fff', borderColor: '#6393C1' };

const DAY_BOX_DISABLED: React.CSSProperties = { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' };

const dayBox = (key: string | number, label: React.ReactNode, selected: boolean, onClick: () => void, disabled: boolean = false) => (
    <div
        key={key}
        style={{ ...DAY_BOX_BASE, ...(selected ? DAY_BOX_SELECTED : {}), ...(disabled ? DAY_BOX_DISABLED : {}) }}
        onClick={disabled ? undefined : onClick}
    >
        {label}
    </div>
);

// Mirrors the Jobs_Prospect window - one form for both Add Job and Edit Job (Oracle's own :action
// item is just a read-only "Add Job"/"Edit Job" label, reflected here as the dialog title instead).
// Report Frequency drives a conditional sub-picker (WHEN-LIST-CHANGED oid 581: Show_View('Daily'/
// 'Weekly'/'Monthly')) - Weekly shows a single-select day-of-week box picker; Monthly shows a "Day"
// fieldset (Custom/Last/First) where Last/First map to dayNo 99/1 (Last mirrors Oracle's own Last
// Day of Month sentinel) and Custom reveals a 30-box day-of-month grid. The whole conditional area
// sits in a fixed-minHeight container (FREQUENCY_DETAIL_MIN_HEIGHT) sized to the tallest state
// (Monthly + Custom) so switching Frequency never resizes the dialog. Daily shows nothing.
const CostBreakdownJobDialog = (props: ICostBreakdownJobDialogProps) => {
    const { register, watch, getValues } = useForm();
    const frequency = watch('frequency', props.editingJob?.frequency ?? 'D');
    const [weekday, setWeekday] = useState<number | undefined>(
        props.editingJob?.frequency === 'W' ? props.editingJob?.dayNo : undefined,
    );
    const [monthDay, setMonthDay] = useState<number | undefined>(
        props.editingJob?.frequency === 'M' && props.editingJob?.dayNo !== 99 && props.editingJob?.dayNo !== 1
            ? props.editingJob?.dayNo
            : undefined,
    );
    const [monthDayType, setMonthDayType] = useState<'custom' | 'last' | 'first'>(() => {
        if (props.editingJob?.frequency !== 'M') { return 'custom'; }
        if (props.editingJob?.dayNo === 99) { return 'last'; }
        if (props.editingJob?.dayNo === 1) { return 'first'; }
        return 'custom';
    });
    const selectedCount = Object.values(props.selected).filter(Boolean).length;

    const handleSave = () => {
        const values = getValues();
        let dayNo: number | undefined;
        if (values.frequency === 'W') {
            dayNo = weekday;
        } else if (values.frequency === 'M') {
            dayNo = monthDayType === 'last' ? 99 : monthDayType === 'first' ? 1 : monthDay;
        }
        props.onSave({
            clNo: Number(values.clientNumber),
            email: values.email,
            cc: values.cc,
            frequency: values.frequency,
            dayNo,
            active: values.active === 'Y',
            includeAdmin: values.includeAdmin === 'Y',
        });
    };

    return (
        <Dialog title={props.editingJob ? 'Edit Job' : 'Add Job'} dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '55%' }}>
            <div className="form-row" style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '.75rem' }}>
                <div style={{ width: '8rem', marginRight: '.25rem' }}>
                    <RHFInputBox
                        label="Client No."
                        labelStyle={{ whiteSpace: 'nowrap' }}
                        name="clientNumber"
                        type="number"
                        register={register}
                        defaultVal={props.initialClientNumber ? String(props.initialClientNumber) : ''}
                        onBlur={(e) => props.onClientNumberBlur(e.currentTarget.value)}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                </div>
                <Button
                    reactIcon={FaSearch}
                    iconStyle={{ width: '1.5rem', height: '1.5rem' }}
                    iconHoverColor="blue"
                    title="Find Client"
                    style={{ height: '3rem', marginRight: '1rem', background: 'transparent' }}
                    text=""
                    onClick={() => props.onFindClient()}
                />
                <div style={{ minWidth: '12rem' }}>
                    {props.client && <div style={{ fontWeight: 'bold' }}>{props.client.clientNumber} - {props.client.clientName}</div>}
                </div>
            </div>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
                <div style={{ flex: '3 1 0' }}>
                    <div style={{ overflowY: 'scroll', maxHeight: '20rem', border: '1px solid #ccc' }}>
                        <table style={{ width: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '10%' }} />
                                    <th style={{ width: '20%' }}>No.</th>
                                    <th style={{ textAlign: 'left' }}>Name</th>
                                </tr>
                            </thead>
                            <tbody>
                                {props.prospects.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="bold-centered">Enter or find a Client No. to list its projects.</td>
                                    </tr>
                                )}
                                {props.prospects.map((prospect) => (
                                    <tr
                                        key={prospect.prId}
                                        className="table-item-hover"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => props.onToggleProspect(prospect.prId)}
                                    >
                                        <td style={{ textAlign: 'center' }}>
                                            <input type="checkbox" checked={!!props.selected[prospect.prId]} readOnly={true} />
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{prospect.prNo}</td>
                                        <td>{prospect.prName}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ textAlign: 'center', color: '#666', marginTop: '.25rem' }}>{selectedCount} of {props.prospects.length} project(s) selected</div>
                </div>
                <div style={{ flex: '2 1 0' }}>
                    <RHFSelect
                        label="Report Frequency"
                        labelOnLeft={true}
                        name="frequency"
                        register={register}
                        options={FREQUENCY_OPTIONS}
                        defaultVal={props.editingJob?.frequency ?? 'D'}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%', marginBottom: '.75rem' }}
                    />
                    <div style={{ minHeight: FREQUENCY_DETAIL_MIN_HEIGHT, marginBottom: '.75rem' }}>
                        {frequency === 'W' && (
                            <div>
                                <div style={{ marginBottom: '.25rem', color: '#666' }}>Day of Week</div>
                                <div style={{ display: 'flex', gap: '.4rem' }}>
                                    {WEEKDAY_BOXES.map((w) => dayBox(w.day, w.label, weekday === w.day, () => setWeekday(w.day)))}
                                </div>
                            </div>
                        )}
                        {frequency === 'M' && (
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '.4rem', maxWidth: '18rem' }}>
                                    {MONTH_DAY_BOXES.map((d) => dayBox(d, d, monthDay === d, () => setMonthDay(d), monthDayType !== 'custom'))}
                                </div>
                                <fieldset style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '.5rem .75rem' }}>
                                    <legend style={{ padding: '0 .3rem', color: '#666' }}>Day</legend>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                                        {MONTH_DAY_TYPE_OPTIONS.map((opt) => (
                                            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '.25rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                <input
                                                    type="radio"
                                                    name="monthDayType"
                                                    checked={monthDayType === opt.value}
                                                    onChange={() => setMonthDayType(opt.value)}
                                                />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </fieldset>
                            </div>
                        )}
                    </div>
                    <RHFSelect
                        label="Include Admin?"
                        labelOnLeft={true}
                        name="includeAdmin"
                        register={register}
                        options={YES_NO_OPTIONS}
                        defaultVal={props.editingJob?.includeAdmin ? 'Y' : 'N'}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%', marginBottom: '.75rem' }}
                    />
                    <RHFSelect
                        label="Active Job?"
                        labelOnLeft={true}
                        name="active"
                        register={register}
                        options={YES_NO_OPTIONS}
                        defaultVal={props.editingJob === undefined || props.editingJob?.active ? 'Y' : 'N'}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%', marginBottom: '.75rem' }}
                    />
                    <RHFInputBox
                        label="Email To"
                        labelOnLeft={true}
                        name="email"
                        register={register}
                        defaultVal={props.editingJob?.email ?? ''}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%', marginBottom: '.75rem' }}
                    />
                    <RHFInputBox
                        label="CC"
                        labelOnLeft={true}
                        name="cc"
                        register={register}
                        defaultVal={props.editingJob?.cc ?? ''}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                </div>
            </div>
            <div className="form-row" style={{ marginTop: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button className="blue-hover-button" style={{ marginRight: '1rem', marginBottom: '.5rem' }} text="Select All" onClick={() => props.onSelectAll()} />
                <Button className="blue-hover-button" style={{ marginRight: '1rem', marginBottom: '.5rem' }} text="Deselect All" onClick={() => props.onDeselectAll()} />
                <Button className="blue-hover-button" style={{ marginRight: '1rem', marginBottom: '.5rem' }} text="Cancel" onClick={() => props.onCancel()} />
                <Button className="blue-hover-button" style={{ marginBottom: '.5rem' }} text="Add / Update Job" onClick={() => handleSave()} />
            </div>
        </Dialog>
    );
};

export default CostBreakdownJobDialog;



// WEBPACK FOOTER //
// ./src/components/project-billing/dialogs/CostBreakdownJobDialog.tsx